/**
 * The last line before a white screen.
 *
 * A crash inside a component used to unmount the whole tree and leave the analyst looking at
 * nothing — no message, no way back, and nothing to tell soporte. Here it becomes a page that says
 * what happened, offers the one useful action, and keeps the technical detail one click away.
 */

import { Component, type ErrorInfo, type ReactNode } from "react";

interface State {
  error: Error | null;
}

export default class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Nothing from a legajo goes to the console: the stack is enough to find the bug, and this
    // screen may be shared with soporte.
    console.error("Fallo la interfaz:", error.message, info.componentStack);
  }

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="flex h-full items-center justify-center p-8">
        <div className="max-w-lg">
          <h1 className="text-lg font-semibold">La pantalla no se pudo mostrar</h1>
          <p className="mt-2 text-sm text-muted">
            Es un problema de la interfaz, no de tu legajo: nada de lo que hiciste se perdió.
            Recargá la página; si vuelve a pasar, avisá a soporte con la hora y qué estabas mirando.
          </p>
          <div className="mt-4 flex gap-2">
            <button
              onClick={() => window.location.reload()}
              className="rounded-lg bg-primary px-3 py-2 text-sm font-medium text-white hover:bg-primary-hover"
            >
              Recargar
            </button>
            <button
              onClick={() => {
                this.setState({ error: null });
                window.location.assign("/");
              }}
              className="rounded-lg border border-line px-3 py-2 text-sm hover:bg-canvas"
            >
              Volver al inicio
            </button>
          </div>
          <details className="mt-4 text-xs text-muted">
            <summary className="cursor-pointer">Detalle técnico</summary>
            <pre className="mt-2 overflow-x-auto rounded-lg border border-line bg-canvas p-3">
              {error.message}
            </pre>
          </details>
        </div>
      </div>
    );
  }
}
